import { useAuth } from '@agentnet/auth';
import { useFeatureFlag } from '@agentnet/feature-flags';
import { HashUtils } from '@agentnet/utils';
import { pxToRem } from '@fluentsms/agentnet-web-components';
import {
  FormControlLabel,
  Grid,
  IconButton,
  Paper,
  Switch,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  Typography,
} from '@material-ui/core';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import { ReportProblemOutlined } from '@material-ui/icons';
import { Alert } from '@material-ui/lab';
import { GetFileInfoProps } from 'api/file/file-api';
import { getEndorsements, getRateTypes } from 'api/jacket-api';
import {
  assignCplApi,
  assignPolicyApi,
  deleteFileNrop,
  getFileMiscByFileId,
  getFileNropsByFileId,
  updateFileMisc,
  updateFileNrop,
} from 'api/orderManagement/order-management-api';
import { addRemoveQueue, calculateReports, getBulkRateTypes, ReportFiles, setAutoReport } from 'api/reports/api';
import {
  jacketPremium,
  JacketStatus,
  policyFieldsWithNameInLabel,
  ratesFeesCountyRequired,
} from 'features/jackets/constants';
import { Endorsement, PolicyField, PolicyFieldOption, RateTypes } from 'features/jackets/types';
import { orderStatuses } from 'features/Remittance/reportsListConfig';
import { BulkRateTypePayload, BulkRateTypeResponse, Policy, SingleRateTypePayload } from 'features/Remittance/types';
import { APIStatus } from 'features/serviceorder/constants';
import { FileDataContext, FileDataContextInterface } from 'hooks/FileDataContext';
import { ProfileContext, ProfileContextInterface } from 'hooks/ProfileContext';
import useAsync from 'hooks/useAsync';
import { useNavPermissions } from 'hooks/useNavPermissions';
import * as _ from 'lodash';
import React, { ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import AgentNetButton from 'ui-kit/components/button/AgentNetButton';
import FormDrawerComponent from 'ui-kit/components/drawer/FormDrawerComponent';
import LoadingSpinner from 'ui-kit/components/LoadingSpinner';
import useGlobalMessages from 'ui-kit/components/notification/useGlobalMessages';
import useSnackBars from 'ui-kit/components/notification/useSnackbars';
import ReportedFeesTable from 'ui-kit/form/ReportedFeesTable';
import NoProducts from 'ui-kit/icons/NoProducts';
import NoRatesIcon from 'ui-kit/icons/NoRates';
import { AgentNetCheckBox } from 'ui-kit/inputs';
import { formatDate } from 'utilities/dateFormatter';
import { calculateTotals, dollarize, makeRandomId, policyCalculateTotals, removeEmpty } from 'utilities/utilities';
import { CalcCriteriaPolicySchema, FileLevelSettings, isValidDate } from 'utilities/validation/schemas/jacket-schema';
import { doValidate, FieldValidationError } from 'utilities/validation/validation';
import {
  AssignCplRequestModel,
  AssignCplResponseModel,
  AssignJacketRequest,
  FileMisc,
  FileNrop,
} from '../../../features/orderManagement/types';
import { FeatureFlag } from '../../../utilities/constants/launch-darkly-flags';
import CalcCriteriaTab, { CplType } from './CalcCriteriaTab';
import {
  applyRateFeeAmountValidation,
  findFirstRateFeeAmountError,
  RATE_FEE_AMOUNT_ERROR_MESSAGES,
  RateFeeReportedField,
  syncGrossAndRemittanceBasis,
  withRateFeeLineItemValidation,
} from './ReportsAndPayDrawer.utils';
import { VirtualJacket } from './VirtualJacketCard';
import FSCTab, { NropAmountsType } from './FSCTab';

interface ReportsAndPayDrawerProps {
  openDrawer: boolean;
  setOpenDrawer: (isOpen: boolean) => void;
  drawerData: any;
  isFromFile?: boolean;
  setDrawerData: any;
  getUpdatedData?: any;
  setFileTab?: any;
  rateAndFeeExecute: any;
  hasViewPayRight?: boolean;
  onReport?: () => void;
  disableFields?: boolean;
  loadFileData?: (fileProps: GetFileInfoProps, callback?: () => void) => void;
}
type ApiStatus = 'idle' | 'pending' | 'success' | 'error' | 'loading';
interface IDrawerData {
  showBackDropOnDrawer?: boolean;
  rateAndFeeStatus?: ApiStatus;
  calcCriteriaStatus?: ApiStatus;
  payAndInvoiceStatus?: ApiStatus;
  noRateAndFeeExists?: boolean;
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    tabWrap: {
      borderBottom: `1px solid #e5e5e5`,
      width: '105%',
      margin: '-46px 0 0 -24px',
    },
    tabText: {
      textTransform: 'none',
      fontSize: pxToRem(14),
    },
    switch: {
      marginTop: '8px',
      marginBottom: '8px',
      marginLeft: '8px',
      justifyContent: 'left',
      '& span': {
        fontWeight: 'bold',
      },
    },
    standAlone: {
      marginTop: '12px',
    },
    tabsRoot: {
      marginLeft: theme.spacing(3),
      '& .MuiTab-root': {
        padding: theme.spacing(2),
        '&.Mui-selected .MuiTypography-root': {
          fontWeight: 600,
          color: theme.palette.text.primary,
        },
      },
    },
    noProducts: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      textAlign: 'center',
      height: '300px',
      gap: '24px',
      flexDirection: 'column',
      width: '100%',
      marginTop: '16px',
    },
    text: {
      fontWeight: 500,
      fontSize: '16px',
    },
    subtext: {
      fontWeight: 400,
      fontSize: '14px',
      color: '#666',
      lineHeight: '20px',
      marginTop: '8px',
    },
    actions: {
      display: 'flex',
      alignItems: 'flex-start',
      justifyContent: 'center',
      gap: '16px',
    },
    priceOverridenMsg: {
      fontStyle: 'italic',
    },
    remittedProductMessage: {
      margin: '8px 10px 8px 8px',
    },
    notesHeading: {
      fontWeight: 'bold',
      margin: '8px 10px 16px 8px',
      width: '100%',
    },
    notesContainer: {
      marginBottom: '28px',
    },
    notesMsg: {
      margin: '8px 10px 0 8px',
    },
    alertContainer: {
      marginBottom: '62px',
    },
    alertMsg: {
      width: '-webkit-fill-available !important',
      maxWidth: 'unset !important',
      position: 'absolute',
      bottom: '49px',
      left: 0,
      padding: '6px 24px',
      zIndex: 3,
      borderTop: '1px solid #c4c4c4',
      borderRadius: 0,
    },
    bold: {
      fontWeight: 500,
    },
    autoReport: {
      width: '100%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      margin: theme.spacing(2, 0),
    },
    policyNetPremiumNotes: {
      padding: pxToRem(10, 0, 0, 10),
    },
    sharedServicesSection: {
      marginTop: theme.spacing(1),
    },
    splitsTable: {
      marginBottom: theme.spacing(2),
      '& .MuiTableCell-root': {
        borderBottom: `1px solid ${theme.palette.divider}`,
        padding: theme.spacing(0.75, 2),
      },
      '& .MuiTableCell-head': {
        fontWeight: 600,
        backgroundColor: theme.palette.grey[100],
      },
    },
    splitNameCell: {
      width: '50%',
    },
    splitPercentageCell: {
      width: '25%',
      textAlign: 'right',
    },
    splitAmountCell: {
      width: '25%',
      textAlign: 'right',
    },
    splitPercentageContent: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'flex-end',
    },
    splitInput: {
      width: 100,
      textAlign: 'right',
      border: 'none',
      outline: 'none',
      backgroundColor: 'transparent',
      fontSize: 'inherit',
      fontFamily: 'inherit',
      padding: 0,
      '&::-webkit-outer-spin-button, &::-webkit-inner-spin-button': {
        '-webkit-appearance': 'none',
        margin: 0,
      },
      '&[type=number]': {
        '-moz-appearance': 'textfield',
      },
    },
    totalRow: {
      '& .MuiTableCell-root': {
        fontWeight: 600,
      },
    },
    totalInvalid: {
      color: theme.palette.error.main,
    },
  }),
);

const ADMIN_ROLE_ID = 3;
const OBO_ROLE_ID = 5;
const FSC_ROLE_ID = 14;

const DECIMAL_PRECISION = 1000000;
const NEAR_ZERO_TOLERANCE = 0.0000005;

const SPLIT_FIELDS = [
  { key: 'escrowSplit', label: 'Escrow Premium' },
  { key: 'agentSplit', label: 'Local Agent Premium' },
  { key: 'stateAgencySplit', label: 'State Agency Premium' },
  { key: 'nationalAgentSplit', label: 'National Agent Premium' },
] as const;

type SplitFieldKey = (typeof SPLIT_FIELDS)[number]['key'];

const cloneRateAndFeeDataWithValidation = (rateAndFeeData: any) =>
  withRateFeeLineItemValidation(_.cloneDeep(rateAndFeeData));

const ReportsAndPayDrawer = ({
  openDrawer,
  setOpenDrawer,
  drawerData,
  setDrawerData,
  getUpdatedData,
  setFileTab,
  isFromFile = false,
  rateAndFeeExecute,
  onReport,
  hasViewPayRight = false,
  disableFields = false,
  loadFileData,
}: ReportsAndPayDrawerProps): JSX.Element => {
  const fileDataCtx: FileDataContextInterface = useContext(FileDataContext) ?? {};
  const { fileData, triggerFileRefresh } = fileDataCtx;
  const history = useHistory();
  const classes = useStyles();
  const [tabValue, setTabValue] = useState(drawerData.tab);
  const [fileName, setfileName] = useState(drawerData.name);
  const [isDiscardError, setDiscardError] = useState(false);
  const [isFormValid, setFormValid] = useState(false);
  const [isUnqueue, setUnqueue] = useState(false);
  const [reportDetails, setReport] = useState({
    isReport: false,
    fromReport: false,
  });
  const [showRemittedProduct, setShowRemittedProduct] = useState(false);
  const [showValidation, setShowValidation] = useState(false);
  const [_endorsementsList, setEndorsementsList] = useState<Endorsement[]>([]);
  const [isCalcCriteriaDataUpdated, setCalcCriteriaDataUpdated] = useState(false);
  const [isCalcCriteriaDiscard, setCalcCriteriaDiscard] = useState(false);
  const [displayError, setDisplayError] = useState<boolean>(false);
  const [updateData, setUpdateData] = useState<boolean>(false);
  const [errMsgs, setErrMsgs] = useState([]);
  const [rateAndFeeData, setRateAndFeeData] = useState(() =>
    drawerData?.rateAndFeeData ? cloneRateAndFeeDataWithValidation(drawerData.rateAndFeeData) : [],
  );
  const [calcCriteriaData, setCalcCriteriaData] = useState(_.cloneDeep(drawerData?.calcCriteriaData ?? []));
  const additionalQuestionRef = useRef<any>({});
  const [cplFees, setCplFees] = useState<CplType[]>([]);
  const [virtualJackets, setVirtualJackets] = useState<VirtualJacket[]>([]);
  const [nropAmountsData, setNropAmountsData] = useState<NropAmountsType[]>([]);
  const [fileMisc, setFileMisc] = useState<FileMisc | null>(null);
  const [isFileMiscDirty, setIsFileMiscDirty] = useState<boolean>(false);
  const [splitEditingField, setSplitEditingField] = useState<SplitFieldKey | null>(null);
  const [splitEditValue, setSplitEditValue] = useState<string>('');
  const [calcCriteriaSetValidationError, setCalcCriteriaSeValidationError] = useState<FieldValidationError[]>([]);
  const [calcCriteriaCplValidationError, setCalcCriteriaCplValidationError] = useState<FieldValidationError[]>([]);
  const [calcCriteriaVirtualJacketValidationError, setCalcCriteriaVirtualJacketValidationError] = useState<
    FieldValidationError[]
  >([]);

  // for validation of calc criteria tab
  const [validationError, setValidationError] = useState<FieldValidationError[]>([]);
  const [fileLevelValidationError, setFileLevelValidationError] = useState<FieldValidationError[]>([]);
  const [isRateEffectiveDateMissing, setIsRateEffectiveDateMissing] = useState<boolean>(false);

  const isFscTabEnabled = useFeatureFlag(FeatureFlag.SHOW_FSC_TAB, false);
  const { enabled: removeComplianceDateFutureBlock } = useFeatureFlag(
    FeatureFlag.JACKET_ANS_9001_REMOVE_COMPLIANCE_DATE_FUTURE_BLOCK,
    false,
  );
  const jacketValidationContext = useMemo(
    () => ({ removeComplianceDateFutureBlock }),
    [removeComplianceDateFutureBlock],
  );

  const { getAccessToken } = useAuth();
  const { addGlobalMsg } = useGlobalMessages();
  const profileCtx: ProfileContextInterface = useContext(ProfileContext) ?? {};
  const { profile, userFirm } = profileCtx;
  const isFscUser = profile?.roleId === FSC_ROLE_ID;

  const hideReportsAndPay = useMemo(() => {
    const payRights = profile?.activityRights?.find(
      (rights) => rights.ActivityRightId === 61 || rights.ActivityRightId === 83,
    );
    const remitRights = profile?.activityRights?.find((rights) => rights.ActivityRightId === 33);
    return isFromFile && !remitRights ? !payRights || !remitRights : !payRights && remitRights;
  }, [profile?.activityRights, isFromFile]);

  const hasCalcCriteriaTabChanges = useMemo(() => {
    return isCalcCriteriaDataUpdated || cplFees.length > 0 || virtualJackets.length > 0;
  }, [isCalcCriteriaDataUpdated, cplFees.length, virtualJackets.length]);

  const updateFileMiscApi = async (): Promise<void> => {
    const token = await getAccessToken();
    if (fileMisc) {
      await updateFileMisc(token, fileMisc);
    }
  };

  const { execute: executeUpdateFileMisc, status: updateFileMiscStatus } = useAsync<void>(updateFileMiscApi, false);

  const getFileMiscApi = async (fileId: number): Promise<FileMisc> => {
    const token = await getAccessToken();
    return await getFileMiscByFileId(token, fileId);
  };

  const {
    execute: executeGetFileMisc,
    status: getFileMiscStatus,
    value: getFileMiscValue,
  } = useAsync<FileMisc>(getFileMiscApi, false);

  const updateFileNropApi = async (): Promise<void> => {
    const token = await getAccessToken();
    for (const nropAmount of nropAmountsData) {
      if (nropAmount.isDirty) {
        await updateFileNrop(token, nropAmount);
      }
    }
  };

  const { execute: executeUpdateFileNrop, status: updateFileNropStatus } = useAsync<void>(updateFileNropApi, false);

  const deleteFileNropApi = async (): Promise<void> => {
    const token = await getAccessToken();
    for (const nropAmount of nropAmountsData) {
      if (nropAmount.isDeleted && nropAmount.fileId && nropAmount.fileNropId) {
        await deleteFileNrop(token, nropAmount.fileId, nropAmount.fileNropId);
      }
    }
  };

  const { execute: executeDeleteFileNrop, status: deleteFileNropStatus } = useAsync<void>(deleteFileNropApi, false);

  const getFileNropsByFileIdApi = async (fileId: number): Promise<FileNrop[]> => {
    const token = await getAccessToken();
    return await getFileNropsByFileId(token, fileId);
  };

  const {
    execute: executeGetFileNropsByFileId,
    status: getFileNropsByFileIdStatus,
    value: getFileNropsByFileIdValue,
  } = useAsync<FileNrop[]>(getFileNropsByFileIdApi, false);

  const reportSelectedFiles = async (): Promise<{ result: any; errorCode?: string | number }> => {
    const token = await getAccessToken();
    const payload: { files: string[] } = { files: [drawerData.fileId] };
    return await ReportFiles(payload, token);
  };
  const {
    execute: executeReportData,
    status: executeReportDataStatus,
    value: reportResults,
    errors: reportErrors,
  } = useAsync<any>(reportSelectedFiles, false);

  const updateRateTypesAndStatCode = (
    policyDetails: PolicyField[],
    policyFields: PolicyField,
    rateTypes: RateTypes[],
  ) => {
    if (policyFields.name === 'RateType') {
      policyFields.options = rateTypes.map((rateTypeOption: RateTypes) => {
        return {
          text: rateTypeOption.rateTypeName,
          value: String(rateTypeOption.rateTypeId),
        };
      });

      const ifValueExists = policyFields.options.find(
        (currentOption: PolicyFieldOption) => currentOption.value === policyFields.value,
      );
      const isDefaultExist = policyFields.options.find(
        (currentOption: PolicyFieldOption) => currentOption.text === 'Basic',
      );
      if (!ifValueExists) {
        policyFields.value = isDefaultExist ? isDefaultExist.value : '';
      }
    }

    if (policyFields.name === 'StatCode') {
      policyFields.value = filterStatCode(policyFields).newValue;
    }
  };

  const fetchRateTypes = async (payload: SingleRateTypePayload, fields: PolicyField[]) => {
    try {
      setDrawerData((prev: IDrawerData) => ({ ...prev, showBackDropOnDrawer: true }));
      const token = await getAccessToken();
      const { formNumber, stateCode, policyEffectiveDate, isExtendedCoverage, countyName, accountNumber } = payload;
      if (formNumber != '') {
        const result = await getRateTypes(
          formNumber,
          stateCode,
          policyEffectiveDate,
          isExtendedCoverage,
          countyName,
          accountNumber,
          token,
        );

        fields.forEach((item: PolicyField) => {
          updateRateTypesAndStatCode(fields, item, result);
        });
      }
    } finally {
      setDrawerData((prev: IDrawerData) => ({ ...prev, showBackDropOnDrawer: false }));
    }
  };

  const fetchBulkRateTypes = async (payload: BulkRateTypePayload, policy: Policy[]) => {
    try {
      setDrawerData((prev: IDrawerData) => ({ ...prev, showBackDropOnDrawer: true }));
      const token = await getAccessToken();
      const result = await getBulkRateTypes(token, payload);

      result.forEach((item: BulkRateTypeResponse) => {
        policy.forEach((policyDetails: Policy) => {
          if (policyDetails.formNumber === String(item.formNumber)) {
            policyDetails.fields.forEach((policyFields: PolicyField) => {
              updateRateTypesAndStatCode(policyDetails.fields, policyFields, item.rateTypes);

              if (policyFields.name === 'IsExtendedCoverage') {
                policyFields.isChecked = item.isExtendedCoverage;
                policyFields.value = String(item.isExtendedCoverage);
              }
            });
          }
        });
      });
    } finally {
      setDrawerData((prev: IDrawerData) => ({ ...prev, showBackDropOnDrawer: false }));
    }
  };

  const assignCplsToFileApi = async (fileId: number): Promise<void> => {
    for (const cplFee of cplFees) {
      if (cplFee.cplId) {
        await assignCplsToFile(fileId, cplFee).catch(() => {
          addGlobalMsg({
            message: 'There was an error while assigning CPL to file.',
            type: 'error',
          });
        });
      }
    }
  };

  const assignCplsToFile = async (fileId: number, cplFee: CplType): Promise<AssignCplResponseModel> => {
    const token = await getAccessToken();
    const request: AssignCplRequestModel = {
      fileId: fileId,
      stateCode: calcCriteriaData.stateCode,
      cplGroupId: cplFee.cplGroupId,
      cplId: cplFee.cplId,
      itemNumber: cplFee.starsItemNumber,
      accountNumber: Number(calcCriteriaData.accountNumber),
      rateItems: [
        {
          isSimul: false,
          faccRateTypeCode: '',
          starsTaxCode: '',
          rateFeeDescr: cplFee.starsItemNumber,
          endorsementStarsItemNumber: '',
          selectedStarsStatCode: '',
          calculatedAmount: cplFee.fee ?? 0,
          netDueAmount: cplFee.fee ?? 0,
          rateFeeTypeCdId: 505, // CPLFE
        },
      ],
    };

    return await assignCplApi(request, token);
  };

  const assignVirtualJacketsToFile = async (fileId: number): Promise<void> => {
    const token = await getAccessToken();

    for (const jacket of virtualJackets) {
      const request: AssignJacketRequest = {
        fileId,
        stateCode: calcCriteriaData.propertyState,
        jacketPrefix: jacket.itemNumber,
        jacketSuffix: jacket.policyNumber.split('-')[1],
        policyNumber: jacket.policyNumber,
        itemNumber: jacket.itemNumber,
        liabilityAmount: jacket.liabilityAmount,
        accountNumber: Number(calcCriteriaData.accountNumber),
        policyDate: formatDate(jacket.policyDate, 'yyyy-MM-dd') || '',
        priorPolicyNumber: undefined,
        priorPolicyDate: jacket.priorPolicyDate
          ? formatDate(jacket.priorPolicyDate, 'yyyy-MM-dd') || undefined
          : undefined,
        priorPolicyAmount: jacket.priorLiabilityAmount || undefined,
        rateItems: [
          {
            isSimul: false,
            faccRateTypeCode: jacket.rateTypeId.toString(),
            starsTaxCode: '',
            rateFeeDescr: jacket.policyNumber,
            endorsementStarsItemNumber: jacket.itemNumber,
            selectedStarsStatCode: jacket.statCode,
            calculatedAmount: jacket.reportedGross,
            netDueAmount: jacket.reportedNet,
            rateFeeTypeCdId: 504,
            isExtendedCoverage: false,
          },
        ],
      };

      try {
        await assignPolicyApi(request, token);
      } catch (error) {
        console.error('Error assigning virtual jacket:', error);
        throw error;
      }
    }
  };

  const saveRateAndFees = async (): Promise<{ result: any; errorCode?: string | number }> => {
    const token = await getAccessToken();
    const payload = { fileId: drawerData.fileId, rateFeeLineItems: rateAndFeeData?.rateFeeLineItems };
    return await calculateReports(token, 'rates-fees', payload);
  };

  const {
    execute: executeCalculate,
    status: executeCalculateStatus,
    value: calculateResults,
    errors: calculateErrors,
  } = useAsync<any>(saveRateAndFees, false);

  const saveCalcCriteria = async (): Promise<{ result: any; errorCode?: string | number }> => {
    const token = await getAccessToken();
    return await calculateReports(token, 'calc-criteria', calcCriteriaData);
  };

  const {
    execute: executeCalcCriteria,
    status: executeCalcCriteriaStatus,
    value: calcCriteriaResults,
    errors: calcCriteriaErrors,
  } = useAsync<any>(saveCalcCriteria, false);

  const toggleAutoReport = async (): Promise<{ result: any; errorCode?: string | number }> => {
    const token = await getAccessToken();
    const payload: { files: string[]; onHold: boolean } = {
      files: [drawerData?.fileId],
      onHold: !rateAndFeeData?.isAutoReportOnHold,
    };
    return await setAutoReport(token, payload);
  };

  const {
    execute: executeAutoReportToggle,
    status: executeAutoReportToggleStatus,
    value: autoReportToggleResults,
    errors: autoReportToggleErrors,
  } = useAsync<any>(toggleAutoReport, false);

  useEffect(() => {
    if (calculateResults) {
      const validatedResults = cloneRateAndFeeDataWithValidation(calculateResults);
      setRateAndFeeData(validatedResults);
      setDrawerData((prev: any) => ({
        ...prev,
        rateAndFeeData: {
          propertyState: validatedResults?.propertyState ?? prev?.rateAndFeeData?.propertyState,
          ...validatedResults,
        },
      }));
      if (isDiscardError) {
        setTabValue(1);
      }
    }
  }, [calculateResults]);

  useEffect(() => {
    if (executeAutoReportToggleStatus === 'error') {
      const prevSwitchValue = !rateAndFeeData?.isAutoReportOnHold;
      setRateAndFeeData((prev: any) => ({ ...prev, isAutoReportOnHold: prevSwitchValue }));
    }
    if (autoReportToggleResults && executeAutoReportToggleStatus === 'success') {
      setUpdateData(true);
    }
  }, [executeAutoReportToggleStatus]);

  // Handle CalcCriteria validation - runs regardless of active tab
  useEffect(() => {
    const errs = [
      ...validationError.flat(),
      ...calcCriteriaSetValidationError.flat(),
      ...calcCriteriaCplValidationError.flat(),
      ...calcCriteriaVirtualJacketValidationError.flat(),
      ...fileLevelValidationError.flat(),
    ];
    setFormValid(errs.length === 0);
  }, [
    validationError,
    calcCriteriaSetValidationError,
    calcCriteriaCplValidationError,
    calcCriteriaVirtualJacketValidationError,
    fileLevelValidationError,
  ]);

  // Handle validation changes from CalcCriteriaTab
  const handleCalcCriteriaValidationChange = (
    seErrors: FieldValidationError[],
    cplErrors: FieldValidationError[],
    virtualJacketErrors: FieldValidationError[],
  ) => {
    setCalcCriteriaSeValidationError(seErrors);
    setCalcCriteriaCplValidationError(cplErrors);
    setCalcCriteriaVirtualJacketValidationError(virtualJacketErrors);
  };

  // Initialize validation when calcCriteriaData loads (before tab is even visited)
  useEffect(() => {
    if (calcCriteriaData && drawerData.calcCriteriaStatus === APIStatus.success) {
      const errs = [
        ...validationError.flat(),
        ...calcCriteriaVirtualJacketValidationError.flat(),
        ...fileLevelValidationError.flat(),
      ];
      setFormValid(errs.length === 0);
    }
  }, [
    calcCriteriaData,
    drawerData.calcCriteriaStatus,
    validationError,
    calcCriteriaVirtualJacketValidationError,
    fileLevelValidationError,
  ]);

  const isPricingValid = useMemo(() => {
    const hasPriceValid = rateAndFeeData?.isPricingValid || calcCriteriaData?.isPricingValid;
    return hasPriceValid || (rateAndFeeData?.hasRemittedProduct && hasPriceValid);
  }, [rateAndFeeData?.isPricingValid, rateAndFeeData?.hasRemittedProduct, calcCriteriaData?.isPricingValid]);

  const isRemitValid = useMemo(
    () => rateAndFeeData?.isRemitValid || calcCriteriaData?.isRemitValid,
    [rateAndFeeData?.isRemitValid, calcCriteriaData?.isRemitValid],
  );

  const getAdditionalQuestionDetails = (oldData: any, newData: any) => {
    let oldQuestionList = [];
    let newQuestionList = [];

    if (oldData?.jacketRates?.length > 0) {
      oldQuestionList = oldData.jacketRates.map((items: any) => items.questions || []).flat();
      newQuestionList = newData.jacketRates.map((items: any) => items.questions || []).flat();
    }

    if (oldData?.standAloneEndorsementRates?.endorsements) {
      newQuestionList = [...newQuestionList, ...(newData?.standAloneEndorsementRates?.questions ?? [])];
      oldQuestionList = [...oldQuestionList, ...(oldData?.standAloneEndorsementRates?.questions ?? [])];
    }

    if (oldData?.cplRates?.length > 0) {
      oldQuestionList = [...oldQuestionList, ...oldData.cplRates.map((items: any) => items.questions || []).flat()];
      newQuestionList = [...newQuestionList, ...newData.cplRates.map((items: any) => items.questions || []).flat()];
    }

    const oldQuestionIds = oldQuestionList.map((question: any) => question.randomId);
    const newQuestionIds = newQuestionList.map((question: any) => question.randomId);

    const isBothEqual = _.isEqual(oldQuestionIds, newQuestionIds);
    const differenceBtwBoth = _.difference(newQuestionIds, oldQuestionIds);

    return {
      isBothEqual,
      elementIdToScroll: differenceBtwBoth[0],
    };
  };

  const handleScroll = (currentElement: any) => {
    if (currentElement) {
      const headerHeight = 70;
      const rootElement = document.querySelector('[data-testid="drawer-content"]');
      currentElement.scrollIntoView();
      if (rootElement) rootElement.scrollBy({ top: -headerHeight, behavior: 'smooth' });
      additionalQuestionRef.current = {};
      addSnackbarMessage({
        message: `Calculation requires Additional questions to be answered`,
        type: 'error',
      });
    }
  };

  useEffect(() => {
    if (calcCriteriaResults && executeCalcCriteriaStatus === APIStatus.success) {
      const { isBothEqual, elementIdToScroll } = getAdditionalQuestionDetails(calcCriteriaData, calcCriteriaResults);
      setCalcCriteriaData(_.cloneDeep(calcCriteriaResults));
      setDrawerData((prev: any) => ({
        ...prev,
        calcCriteriaData: _.cloneDeep(calcCriteriaResults),
      }));

      setUpdateData(true);

      if (isBothEqual || !calcCriteriaResults.hasUnansweredQuestions) {
        rateAndFeeExecute().then(() => {
          setTabValue(0);
        });
      } else {
        const renderTimeout = setTimeout(() => {
          handleScroll(additionalQuestionRef?.current?.[elementIdToScroll as string]);
          clearTimeout(renderTimeout);
        }, 100);
      }
    }
  }, [calcCriteriaResults, executeCalcCriteriaStatus, calcCriteriaResults]);

  const addInQueue = async (): Promise<{ result: any; errorCode?: string | number }> => {
    const token = await getAccessToken();
    const payload: { files: string[] } = { files: [drawerData.fileId] };
    return await addRemoveQueue(token, payload, 'add');
  };
  const removeFromQueue = async (): Promise<{ result: any; errorCode?: string | number }> => {
    const token = await getAccessToken();
    const payload: { files: string[] } = { files: [drawerData.fileId] };
    return await addRemoveQueue(token, payload, 'remove');
  };

  const { addSnackbarMessage } = useSnackBars();

  const fetchEndorsements = async (): Promise<Endorsement[]> => {
    const token = await getAccessToken();
    return getEndorsements(
      drawerData?.calcCriteriaData?.propertyState ?? '',
      drawerData?.calcCriteriaData?.underwriterCode ?? '',
      token,
    );
  };

  const { execute: executeGetEndorsements, value: getEndorsementsValue } = useAsync<Endorsement[]>(
    fetchEndorsements,
    false,
  );

  const {
    execute: executeAddQueue,
    status: executeAddQueueStatus,
    value: queueResult,
    errors: queueErrors,
  } = useAsync<any>(addInQueue, false);

  const {
    execute: executeUnqueue,
    status: executeUnqueueStatus,
    value: unqueueResult,
    errors: unqueueErrors,
  } = useAsync<any>(removeFromQueue, false);

  const closeDrawer = () => {
    if (updateData) {
      getUpdatedData?.();
      setUpdateData(false);
    }
    setUnqueue(false);
    setOpenDrawer(false);
    setDiscardError(false);
    setCalcCriteriaDiscard(false);
    setShowRemittedProduct(false);
    setShowValidation(false);
    setValidationError([]);
    setDrawerData({});
    setEndorsementsList([]);
    setRateAndFeeData([]);
    setCalcCriteriaData([]);
    setIsRateEffectiveDateMissing(false);
    setReport({
      isReport: false,
      fromReport: false,
    });

    if (isFromFile) {
      history.replace(window.location.pathname);
      if (window.location.pathname.includes('file-info')) {
        loadFileData?.({ fileId: drawerData.fileId });
      } else if (window.location.pathname.includes('jackets')) {
        // Re-fetch jackets and standalone endorsements so the jackets page re-evaluates
        // whether "Add Jacket" is permitted after standalone endorsement changes.
        triggerFileRefresh?.(true);
      }
    }
  };

  const initializeCalcCriteriaData = () => {
    const initialData = _.cloneDeep(drawerData.calcCriteriaData);
    let isDataManuallyUpdated = false;
    if (initialData?.standAloneEndorsementRates?.endorsements) {
      initialData.standAloneEndorsementRates.endorsements.forEach((endorsement: any) => {
        if (endorsement?.fields?.[0] && endorsement?.fields?.[0]?.options?.length === 1) {
          isDataManuallyUpdated = true;
          endorsement.fields[0].value = endorsement.fields[0].options[0].value;
        }
      });
    }

    if (initialData?.jacketRates) {
      initialData.jacketRates.forEach((jacketRate: any) => {
        (jacketRate?.endorsements ?? []).forEach((endorsement: any) => {
          if (endorsement?.fields?.[0] && endorsement?.fields?.[0]?.options?.length === 1) {
            isDataManuallyUpdated = true;
            endorsement.fields[0].value = endorsement.fields[0].options[0].value;
          }
        });
      });
    }

    if (isDataManuallyUpdated) {
      console.warn('Data is updated manually, unsaved changes.');
    }

    setCalcCriteriaData(_.cloneDeep(initialData));
  };

  const handleValueReset = () => {
    if (tabValue === 0) {
      setDiscardError(false);
      setTabValue && setTabValue(1);
      setRateAndFeeData(cloneRateAndFeeDataWithValidation(drawerData.rateAndFeeData));
    } else {
      setCalcCriteriaDiscard(false);
      setFormValid(true);
      setTabValue && setTabValue(0);
      initializeCalcCriteriaData();
    }
  };

  const handleCancel = () => {
    setCplFees([]);
    if (isUnqueue) {
      setUnqueue(false);
    } else {
      closeDrawer();
    }
  };

  const randomId = () => (Math.random() + 1).toString(36).substring(7);

  const updateEndorsemnt = (
    endorsements: any,
    newEndorsement: any,
    id: string | null,
    name: string | null,
    amount: number | null,
    reason: string,
  ) => {
    const currentEndorsements = _.cloneDeep(endorsements);
    let updatedEndorsements;
    switch (reason) {
      case 'remove':
      case 'remove-misc': {
        updatedEndorsements = currentEndorsements?.filter((obj: any) => obj.randomId !== id);
        break;
      }
      case 'update-name': {
        updatedEndorsements = currentEndorsements?.map((obj: any) => {
          if (obj.randomId === id) {
            obj.endorsementName = name ?? '';
          }
          return obj;
        });
        break;
      }
      case 'update-amount': {
        updatedEndorsements = currentEndorsements?.map((obj: any) => {
          if (obj.randomId === id) {
            obj.calculatedAmount = amount ?? 0;
          }
          return obj;
        });
        break;
      }
      case 'update-statcode': {
        updatedEndorsements = currentEndorsements?.map((obj: any) => {
          if (obj.randomId === id) {
            obj.fields[0].value = name ?? '';
          }
          return obj;
        });
        break;
      }
      case 'add': {
        updatedEndorsements = [...currentEndorsements, newEndorsement];
        break;
      }
    }
    return updatedEndorsements;
  };

  const validateErrors = async (data: any, index: number) => {
    let errs: FieldValidationError[] =
      (await doValidate(data, CalcCriteriaPolicySchema, jacketValidationContext)) || [];
    errs = errs.map((x) => {
      if (policyFieldsWithNameInLabel.includes(x.field)) {
        x.message = `${data.policyFields.find((y: any) => y.name === x.field)?.label} ${x.message.split(' ').pop()}`;
      }
      return x;
    });
    setValidationError((prev: any) => {
      const item = [...prev];
      item[index] = errs;
      return item;
    });
  };

  const updateJacketEndorsement = (
    jacketId: string | null,
    id: string | null,
    rflId: number | null,
    name: string | null,
    amount: number | null,
    reason: string,
    type: string,
    starsNumber: string | null = '',
    index: number,
  ) => {
    const oldData = _.cloneDeep(calcCriteriaData);
    const currentEndorsements =
      type === 'incorporatedEndorsements'
        ? oldData.jacketRates[index].incorporatedEndorsements
        : oldData.jacketRates[index].endorsements;

    const isMiscellaneous = reason === 'add' && name === 'Miscellaneous Endorsement';
    const newEndorsement = {
      endorsementFormNumber: isMiscellaneous ? 'MISC. END.' : starsNumber ?? '',
      randomId: randomId(),
      isIncorporated: false,
      endorsementName: isMiscellaneous ? '' : name ?? '',
      calculatedAmount: null,
      endorsementId: null,
      group: null,
      isChecked: null,
      endorsementInputFields: null,
      pricingDetails: null,
      fields: [
        {
          name: 'StatCode',
          label: 'Stat Code',
          value: '',
          dataType: 'select',
          group: '',
          order: 4,
          section: 0,
          isRequired: false,
          isChecked: false,
          isDisabled: false,
          options: [],
        },
      ],
    };

    const updatedEndorsement = updateEndorsemnt(currentEndorsements, newEndorsement, id, name, amount, reason);

    if (type === 'incorporatedEndorsements') {
      oldData.jacketRates[index].incorporatedEndorsements = updatedEndorsement;
    } else {
      oldData.jacketRates[index].endorsements = updatedEndorsement;
    }

    setCalcCriteriaData(oldData);
  };

  const updateSaEndorsement = (
    jacketId: string | null,
    id: string | null,
    rflId: number | null,
    name: string | null,
    amount: number | null,
    reason: string,
    type: string,
    starsNumber: string | null = '',
  ) => {
    const oldData = _.cloneDeep(calcCriteriaData);

    if (!oldData?.standAloneEndorsementRates) {
      oldData.standAloneEndorsementRates = {
        questions: [],
        endorsements: [],
      };
    }

    const currentEndorsements = oldData.standAloneEndorsementRates?.endorsements ?? [];

    const isMiscellaneous = reason === 'add' && name === 'Miscellaneous Endorsement';
    const newEndorsement = {
      id: 0,
      endorsementType: isMiscellaneous ? 'Miscellaneous' : 'Endorsement',
      endorsementName: isMiscellaneous ? '' : name ?? '',
      calculatedAmount: null,
      endorsementFormNumber: isMiscellaneous ? 'MISC. END.' : starsNumber ?? '',
      randomId: randomId(),
      sequenceNumber: null,
      createdBy: null,
      fields: [
        {
          name: 'StatCode',
          label: 'Stat Code',
          value: '',
          dataType: 'select',
          group: '',
          order: 4,
          section: 0,
          isRequired: false,
          isChecked: false,
          isDisabled: false,
          options: [],
        },
      ],
    };

    oldData.standAloneEndorsementRates.endorsements = updateEndorsemnt(
      currentEndorsements,
      newEndorsement,
      id,
      name,
      amount,
      reason,
    );
    setCalcCriteriaData(oldData);
  };

  useEffect(() => {
    const isError =
      executeUnqueueStatus === 'error' ||
      executeAddQueueStatus === 'error' ||
      executeReportDataStatus === 'error' ||
      executeCalculateStatus === 'error' ||
      executeCalcCriteriaStatus === 'error' ||
      executeAutoReportToggleStatus === 'error';
    setDisplayError(isError);
  }, [
    executeUnqueueStatus,
    executeAddQueueStatus,
    executeReportDataStatus,
    executeCalculateStatus,
    executeCalcCriteriaStatus,
    executeAutoReportToggleStatus,
  ]);

  useEffect(() => {
    if (displayError) {
      const errorMessages = [
        ...(unqueueErrors ?? []),
        ...(reportErrors ?? []),
        ...(queueErrors ?? []),
        ...(calculateErrors ?? []),
        ...(calcCriteriaErrors ?? []),
        ...(autoReportToggleErrors ?? []),
      ];
      setErrMsgs(errorMessages);
    } else {
      setErrMsgs([]);
    }
  }, [
    displayError,
    unqueueErrors,
    reportErrors,
    queueErrors,
    calculateErrors,
    calcCriteriaErrors,
    autoReportToggleErrors,
  ]);

  useEffect(() => {
    if (displayError && errMsgs.length) {
      errMsgs?.map((err) => {
        addGlobalMsg({
          message: err,
          type: 'error',
        });
      });
    }
  }, [errMsgs]);

  useEffect(() => {
    if (executeReportDataStatus === 'success') {
      // For Report & Pay (isReport), skip the list refresh in closeDrawer — the user is
      // navigating away to Invoice Summary so any getUpdatedData call would be wasted.
      if (reportDetails.isReport) {
        setUpdateData(false);
      }
      closeDrawer();
      if (reportDetails.fromReport) {
        addSnackbarMessage({
          message: `File Reported: ${drawerData.name}`,
          type: 'success',
        });
        if (hasViewPayRight) {
          history.push('/accounting/remittance/pay');
        } else {
          onReport?.();
          getUpdatedData?.();
        }
      } else if (reportDetails.isReport) {
        history.push({
          pathname: `/accounting/remittance/pay/invoice-summary`,
          state: {
            localFirmID: drawerData?.eventData?.firmId,
            selectedRows: [
              {
                fileOrderIds: reportResults?.fileOrderIds,
                ...drawerData.eventData,
                underwriter: drawerData?.calcCriteriaData?.underwriterCode ?? '',
                getPaymentData: true,
              },
            ],
          },
        });
      }
    }
  }, [executeReportDataStatus]);

  useEffect(() => {
    if (executeCalculateStatus === 'success') {
      addSnackbarMessage({
        message: `Save Successful`,
        type: 'success',
      });
      if (!isFromFile) {
        setUpdateData(true);
      }
    }
  }, [executeCalculateStatus]);

  useEffect(() => {
    if (queueResult) {
      getUpdatedData && getUpdatedData();
      closeDrawer();
      addSnackbarMessage({
        message: `File was queued`,
        type: 'success',
      });
    }
  }, [queueResult]);

  useEffect(() => {
    if (unqueueResult) {
      getUpdatedData && getUpdatedData();
      closeDrawer();
      addSnackbarMessage({
        message: `File was removed from queue`,
        type: 'success',
      });
    }
  }, [unqueueResult]);

  useEffect(() => {
    if (drawerData.tab !== undefined) {
      setTabValue(drawerData.tab);
    }
  }, [drawerData.tab]);

  useEffect(() => {
    if (drawerData.name !== undefined) {
      setfileName(drawerData.name);
    }
  }, [drawerData.name]);

  useEffect(() => {
    if (drawerData.rateAndFeeData !== undefined) {
      setRateAndFeeData(cloneRateAndFeeDataWithValidation(drawerData.rateAndFeeData));
    }
  }, [drawerData.rateAndFeeData]);

  useEffect(() => {
    if (drawerData.calcCriteriaData !== undefined) {
      initializeCalcCriteriaData();
      // if (drawerData?.calcCriteriaData?.underwriterCode && drawerData?.calcCriteriaData?.propertyState)
      //   executeGetEndorsements().then();
    }
  }, [drawerData.calcCriteriaData]);

  useEffect(() => {
    if (getEndorsementsValue) {
      setEndorsementsList([
        ...getEndorsementsValue,
        {
          endorsementName: 'Miscellaneous Endorsement',
          endorsementFormNumber: 'Miscellaneous Endorsement',
          policyCategories: [],
          policyCoverages: [],
        },
      ]);
    }
  }, [getEndorsementsValue]);

  useEffect(() => {
    if (drawerData.calcCriteriaData != undefined && calcCriteriaData !== undefined) {
      setCalcCriteriaDiscard(false);
      const originalData = _.cloneDeep(drawerData.calcCriteriaData);
      const newData = _.cloneDeep(calcCriteriaData);
      const cleanOriginalData = removeEmpty(originalData);
      const cleanNewData = removeEmpty(newData);
      const isDataSame = _.isEqual(cleanOriginalData, cleanNewData);
      setCalcCriteriaDataUpdated(!isDataSame);
    }
  }, [drawerData.calcCriteriaData, calcCriteriaData]);

  const onChangeProduct = (
    val: number,
    rateFeeLineItemId: number,
    field: RateFeeReportedField,
    childId?: number,
    nestedChildId?: number,
  ) => {
    const isRemittanceBasisRate = rateAndFeeData?.isRemittanceBasisRate === true;

    if (childId) {
      if (!nestedChildId) {
        const newObj = rateAndFeeData.rateFeeLineItems.map((obj: any) => {
          if (obj.rateFeeLineItemId === rateFeeLineItemId && obj.additionalFees) {
            const target = obj.additionalFees.find((e: any) => e.rateFeeLineItemId === childId);
            const source = {
              ...target,
              [field]: val,
            };
            if (field === RateFeeReportedField.Gross) {
              source.isGrossOverridden = !(target.grossCalculated === source.grossReported);
              syncGrossAndRemittanceBasis(source, source.grossReported);
            }
            if (field === RateFeeReportedField.Net) {
              source.isNetOverridden = !(target.netCalculated === source.netReported);
            }
            if (field === RateFeeReportedField.RemittanceBasis) {
              source.isRemittanceBasisOverridden = !(
                target.remittanceBasisCalculated === source.remittanceBasisReported
              );
            }
            applyRateFeeAmountValidation(source, isRemittanceBasisRate);

            const newProducts = obj.additionalFees.map((item: any) =>
              item.rateFeeLineItemId === childId ? source : item,
            );
            return {
              ...obj,
              additionalFees: newProducts,
            };
          }
          return obj;
        });
        setRateAndFeeData({ ...rateAndFeeData, rateFeeLineItems: [...newObj] });
      }
      if (nestedChildId) {
        const newObj = rateAndFeeData.rateFeeLineItems.map((parent: any) => {
          if (parent.rateFeeLineItemId === rateFeeLineItemId && parent.additionalFees) {
            const child = parent.additionalFees.find(
              (e: any) => e.rateFeeLineItemId === childId && e.additionalFees && e.additionalFees.length,
            );
            const grandChildren = child.additionalFees.map((ch: any) => {
              if (ch.rateFeeLineItemId === nestedChildId) {
                if (field === RateFeeReportedField.Gross) {
                  ch.isGrossOverridden = !(ch.grossCalculated === val);
                  syncGrossAndRemittanceBasis(ch, val);
                }
                if (field === RateFeeReportedField.Net) {
                  ch.isNetOverridden = !(ch.netCalculated === val);
                }
                if (field === RateFeeReportedField.RemittanceBasis) {
                  ch.isRemittanceBasisOverridden = !(ch.remittanceBasisCalculated === val);
                }
                const updatedChild = {
                  ...ch,
                  [field]: val,
                };
                applyRateFeeAmountValidation(updatedChild, isRemittanceBasisRate);
                return updatedChild;
              }
              return ch;
            });
            const newChild = {
              ...child,
              additionalFees: grandChildren,
            };
            const newChildren = parent.additionalFees.map((item: any) =>
              item.rateFeeLineItemId === childId ? newChild : item,
            );
            return {
              ...parent,
              additionalFees: newChildren,
            };
          }
          return parent;
        });
        setRateAndFeeData({ ...rateAndFeeData, rateFeeLineItems: [...newObj] });
      }
    } else {
      const newObj = rateAndFeeData.rateFeeLineItems.map((obj: any) => {
        if (obj.rateFeeLineItemId === rateFeeLineItemId) {
          if (field === RateFeeReportedField.Gross) {
            obj.isGrossOverridden = !(obj.grossCalculated === val) || (obj.rateFeeType === jacketPremium && val === 0);
            syncGrossAndRemittanceBasis(obj, val);
          }
          if (field === RateFeeReportedField.Net) {
            obj.isNetOverridden = !(obj.netCalculated === val) || (obj.rateFeeType === jacketPremium && val === 0);
          }
          if (field === RateFeeReportedField.RemittanceBasis) {
            obj.isRemittanceBasisOverridden = !(obj.remittanceBasisCalculated === val);
          }
          const updatedObj = {
            ...obj,
            [field]: val,
          };
          applyRateFeeAmountValidation(updatedObj, isRemittanceBasisRate);
          return updatedObj;
        }
        return obj;
      });
      setRateAndFeeData({ ...rateAndFeeData, rateFeeLineItems: [...newObj] });
    }
  };

  const onClear = (
    rateFeeLineItemId: number,
    field: RateFeeReportedField,
    childId?: number,
    nestedChildId?: number,
  ) => {
    const isRemittanceBasisRate = rateAndFeeData?.isRemittanceBasisRate === true;

    if (childId) {
      if (!nestedChildId) {
        const newObj = rateAndFeeData.rateFeeLineItems.map((obj: any) => {
          if (obj.rateFeeLineItemId === rateFeeLineItemId && obj.additionalFees) {
            const target = obj.additionalFees.find((e: any) => e.rateFeeLineItemId === childId);
            const source = {
              ...target,
            };
            if (field === RateFeeReportedField.Gross) {
              source.isGrossOverridden = false;
              source.grossReported = target.grossCalculated;
              syncGrossAndRemittanceBasis(source, source.grossReported);
            }
            if (field === RateFeeReportedField.Net) {
              source.isNetOverridden = false;
              source.netReported = target.netCalculated;
            }
            if (field === RateFeeReportedField.RemittanceBasis) {
              source.isRemittanceBasisOverridden = false;
              source.remittanceBasisReported = target.remittanceBasisCalculated;
            }
            applyRateFeeAmountValidation(source, isRemittanceBasisRate);
            const newProducts = obj.additionalFees.map((item: any) =>
              item.rateFeeLineItemId === childId ? source : item,
            );
            return {
              ...obj,
              additionalFees: newProducts,
            };
          }
          return obj;
        });
        setRateAndFeeData({ ...rateAndFeeData, rateFeeLineItems: [...newObj] });
      }
      if (nestedChildId) {
        const newObj = rateAndFeeData.rateFeeLineItems.map((parent: any) => {
          if (parent.rateFeeLineItemId === rateFeeLineItemId && parent.additionalFees) {
            const child = parent.additionalFees.find(
              (e: any) => e.rateFeeLineItemId === childId && e.additionalFees && e.additionalFees.length,
            );
            const grandChildren = child.additionalFees.map((ch: any) => {
              if (ch.rateFeeLineItemId === nestedChildId) {
                if (field === RateFeeReportedField.Gross) {
                  ch.isGrossOverridden = false;
                  ch.grossReported = ch.grossCalculated;
                  syncGrossAndRemittanceBasis(ch, ch.grossReported);
                }
                if (field === RateFeeReportedField.Net) {
                  ch.isNetOverridden = false;
                  ch.netReported = ch.netCalculated;
                }
                if (field === RateFeeReportedField.RemittanceBasis) {
                  ch.isRemittanceBasisOverridden = false;
                  ch.remittanceBasisReported = ch.remittanceBasisCalculated;
                }
                applyRateFeeAmountValidation(ch, isRemittanceBasisRate);
              }
              return ch;
            });
            const newChild = {
              ...child,
              additionalFees: grandChildren,
            };
            const newChildren = parent.additionalFees.map((item: any) =>
              item.rateFeeLineItemId === childId ? newChild : item,
            );
            return {
              ...parent,
              additionalFees: newChildren,
            };
          }
          return parent;
        });
        setRateAndFeeData({ ...rateAndFeeData, rateFeeLineItems: [...newObj] });
      }
    } else {
      const newObj = rateAndFeeData.rateFeeLineItems.map((obj: any) => {
        if (obj.rateFeeLineItemId === rateFeeLineItemId) {
          if (field === RateFeeReportedField.Gross) {
            obj.isGrossOverridden = false;
            obj.grossReported = obj.grossCalculated;
            syncGrossAndRemittanceBasis(obj, obj.grossReported);
          }
          if (field === RateFeeReportedField.Net) {
            obj.isNetOverridden = false;
            obj.netReported = obj.netCalculated;
          }
          if (field === RateFeeReportedField.RemittanceBasis) {
            obj.isRemittanceBasisOverridden = false;
            obj.remittanceBasisReported = obj.remittanceBasisCalculated;
          }
          applyRateFeeAmountValidation(obj, isRemittanceBasisRate);
          return {
            ...obj,
          };
        }
        return obj;
      });
      setRateAndFeeData({ ...rateAndFeeData, rateFeeLineItems: [...newObj] });
    }
  };

  const omitKeysRecursively: any = (obj: any, keys: any) => {
    return _.isObject(obj) ? _.mapValues(_.omit(obj, keys), (value) => omitKeysRecursively(value, keys)) : obj;
  };

  const hasOverrideFees = (data: any) => {
    const keysToIgnore = [
      'isError',
      'isRemittanceBasisTooHigh',
      'isNetTooHigh',
      'amountErrorType',
      'isAutoReportOnHold',
    ];
    const oldData = omitKeysRecursively(_.cloneDeep(drawerData.rateAndFeeData), keysToIgnore);
    const newData = omitKeysRecursively(_.cloneDeep(data), keysToIgnore);
    return !_.isEqual(oldData, newData);
  };

  const hasNetError = (subfees: any) => {
    if (rateAndFeeData?.propertyState === 'GA' && ![ADMIN_ROLE_ID, OBO_ROLE_ID].includes(profile?.roleId ?? 0)) {
      return false;
    }

    for (const subfee of subfees) {
      if (subfee.isError) {
        return true;
      }

      if (subfee.additionalFees && subfee.additionalFees.length > 0) {
        if (hasNetError(subfee.additionalFees)) {
          return true;
        }
      }
    }
    return false;
  };

  const isPriceOverriden = useMemo(
    () => hasOverrideFees(rateAndFeeData ?? []),
    [rateAndFeeData, drawerData.rateAndFeeData],
  );
  const grossTotal = useMemo(() => {
    if (rateAndFeeData?.rateFeeLineItems?.length) {
      return calculateTotals(rateAndFeeData.rateFeeLineItems, false);
    } else if (rateAndFeeData?.remittedLineItems?.length) {
      return calculateTotals(rateAndFeeData.remittedLineItems, true);
    }
    return { gross: 0, net: 0 };
  }, [rateAndFeeData]);

  const drawerDataPolicyTotal = useMemo(
    () => policyCalculateTotals(drawerData?.rateAndFeeData?.rateFeeLineItems ?? []),
    [drawerData?.rateAndFeeData],
  );

  const isTotalPolicyNetPremiumZero = useMemo(() => {
    const drawerDataHasUnmodifiedGross = drawerData?.rateAndFeeData?.rateFeeLineItems?.some(
      (item: any) =>
        item.rateFeeType === jacketPremium && item.status === JacketStatus.open && item.isGrossOverridden === false,
    );

    return (
      drawerDataPolicyTotal?.net == 0 && drawerDataHasUnmodifiedGross && Boolean(drawerData?.rateAndFeeData?.notes)
    );
  }, [drawerDataPolicyTotal, drawerData?.rateAndFeeData]);

  const showCalculateButton = useMemo(() => {
    // Show Calculate button if FSC user has made changes to shared services
    if (isFscUser && isFileMiscDirty && tabValue === 0) {
      return true;
    }
    if (!isPricingValid || (isPricingValid && !isRemitValid && !isTotalPolicyNetPremiumZero)) {
      return isPricingValid;
    }
    return hasOverrideFees(rateAndFeeData ?? []);
  }, [
    rateAndFeeData,
    drawerData?.rateAndFeeData,
    isPriceOverriden,
    isPricingValid,
    isRemitValid,
    isFscUser,
    isFileMiscDirty,
    tabValue,
  ]);

  useEffect(() => {
    setDiscardError(false);
    setUnqueue(false);
    setReport({
      isReport: false,
      fromReport: false,
    });
  }, [rateAndFeeData]);

  const handleQueue = () => {
    executeAddQueue().then();
  };

  const handleUnqueueConfirmation = () => {
    setUnqueue(true);
    setDiscardError(false);
  };

  const handleReport = () => {
    setReport({
      isReport: false,
      fromReport: true,
    });
  };

  const isMoreInfoRequired = useMemo(
    () => (isPricingValid && isTotalPolicyNetPremiumZero ? false : !isRemitValid),
    [isPricingValid, isRemitValid, isTotalPolicyNetPremiumZero],
  );
  const isOnlyCPLExist = calcCriteriaData?.cplRates?.length > 0 && calcCriteriaData?.jacketRates?.length === 0;
  const showProblemIcon =
    (!isPricingValid || isMoreInfoRequired || !isFormValid) && drawerData.calcCriteriaStatus === APIStatus.success;

  const toShowActionItem =
    tabValue === 0 &&
    isPricingValid &&
    !isUnqueue &&
    !(reportDetails.isReport || reportDetails.fromReport) &&
    !isDiscardError &&
    !showCalculateButton &&
    !showProblemIcon;

  const hideActionItems = isFromFile
    ? toShowActionItem && profile?.activityRights?.find((rights) => rights.ActivityRightId === 33)
    : toShowActionItem;

  const toShowQueueUnqueue = isTotalPolicyNetPremiumZero ? true : drawerData?.rateAndFeeData?.isRemitValid ?? false;

  const actionItems: ReactNode = (
    <>
      {!disableFields &&
        hideActionItems &&
        drawerData.calcCriteriaStatus === APIStatus.success &&
        drawerData.resource !== 'order-management' && (
          <>
            <AgentNetButton
              color="primary"
              data-testid="RatesAndFeesReport"
              onClick={handleReport}
              variant="outlined"
              id="report-action"
              size="small"
              disabled={isTotalPolicyNetPremiumZero}
            >
              Report
            </AgentNetButton>
            {toShowQueueUnqueue && drawerData.resource !== 'order-management' && (
              <>
                {tabValue === 0 &&
                  drawerData.rateAndFeeData.isMyQueue === false &&
                  drawerData.rateAndFeeData.isFirmQueue === false && (
                    <AgentNetButton
                      onClick={handleQueue}
                      data-testid="RatesAndFeesQueue"
                      color="primary"
                      variant="outlined"
                      id="queue-action"
                      size="small"
                      disabled={isTotalPolicyNetPremiumZero}
                    >
                      Queue
                    </AgentNetButton>
                  )}
                {tabValue === 0 && (drawerData.rateAndFeeData.isMyQueue || drawerData.rateAndFeeData.isFirmQueue) && (
                  <AgentNetButton
                    onClick={handleUnqueueConfirmation}
                    data-testid="RatesAndFeesRemoveQueue"
                    color="primary"
                    variant="outlined"
                    id="unqueue-action"
                    size="small"
                    disabled={isTotalPolicyNetPremiumZero}
                  >
                    Remove from Queue
                  </AgentNetButton>
                )}
              </>
            )}
          </>
        )}
    </>
  );

  const primaryActionLabel = React.useMemo(() => {
    if (tabValue === 1 || tabValue === 2) {
      return 'Save & Calculate';
    }
    if (!showCalculateButton) {
      if (isUnqueue) return 'Yes, Remove';
      if (reportDetails.isReport || reportDetails.fromReport) return 'Yes, Report';
    }
    if (showCalculateButton || isDiscardError) {
      return 'Calculate';
    }
    return 'Report & Pay';
  }, [reportDetails.isReport, reportDetails.fromReport, isUnqueue, isDiscardError, showCalculateButton, tabValue]);

  const renderMessage = useCallback(
    (msg: string, severity: 'warning' | 'error' = 'warning', action?: React.ReactNode) => (
      <>
        <div className={classes.alertContainer}></div>
        <Alert className={classes.alertMsg} {...(action && { action })} severity={severity}>
          {msg}
        </Alert>
      </>
    ),
    [],
  );

  const goToJacket = () => {
    closeDrawer();
    setFileTab?.(5);
    history.push(`/files/${drawerData.fileId}/jackets`);
  };

  const warningMessages = () => {
    if (rateAndFeeData?.hasPendingProduct) {
      const action: React.ReactNode = (
        <IconButton aria-label="close" color="inherit" size="small" onClick={goToJacket}>
          <Typography className={classes.bold}>Create Jacket</Typography>
        </IconButton>
      );

      return renderMessage(
        'File contains pending products and cannot be reported.  Please resolve.',
        'warning',
        action,
      );
    }

    if (isDiscardError) {
      const action: React.ReactNode = (
        <IconButton
          aria-label="close"
          color="inherit"
          data-testid="RatesAndFeesDiscardChanges"
          size="small"
          onClick={handleValueReset}
        >
          <Typography className={classes.bold}>Discard Changes</Typography>
        </IconButton>
      );

      return renderMessage('You have unsaved changes on this page.', 'warning', action);
    }

    if (isMoreInfoRequired && !isOnlyCPLExist) {
      const handleMissingInfo = () => {
        goToCalcCriteria();
      };

      const action: React.ReactNode = (
        <IconButton aria-label="close" color="inherit" size="small" onClick={handleMissingInfo}>
          <Typography className={classes.bold}>Enter Required Info</Typography>
        </IconButton>
      );

      return renderMessage(
        'Rates calculated successfully, but additional information is required for reporting.',
        'warning',
        action,
      );
    }

    if (isMoreInfoRequired && isOnlyCPLExist) {
      const action: React.ReactNode = (
        <IconButton aria-label="close" color="inherit" size="small" onClick={goToJacket}>
          <Typography className={classes.bold}>Add New Jacket</Typography>
        </IconButton>
      );

      return renderMessage('At least one remittable product is required to price a file.', 'warning', action);
    }

    if (primaryActionLabel === 'Calculate' && showValidation && hasNetError(rateAndFeeData?.rateFeeLineItems ?? [])) {
      const amountErrorType = findFirstRateFeeAmountError(
        rateAndFeeData?.rateFeeLineItems ?? [],
        rateAndFeeData?.isRemittanceBasisRate === true,
      );
      const message = amountErrorType
        ? RATE_FEE_AMOUNT_ERROR_MESSAGES[amountErrorType]
        : RATE_FEE_AMOUNT_ERROR_MESSAGES.netExceedsGross;
      return renderMessage(message, 'error');
    }

    if (reportDetails.isReport || reportDetails.fromReport) {
      if (drawerData.rateAndFeeData?.propertyState === 'NY')
        return renderMessage(
          `I, ${profile?.firstName} ${profile?.lastName}, of ${userFirm?.name} hereby certify that the Title Premium(s) were properly calculated and found to be accurate. I further certify that only permitted, proper fees and other charges have been made. I further certify that I have provided clear notice to the insured and/or borrower, as required, that the lender or other party may require the performance of additional services which are not necessary services in connection with the issuance of the title policy, and the issuance of the title policy is not dependent upon the performance of such additional services.`,
        );
      return renderMessage(`Reporting 1 file for ${dollarize(grossTotal.net)}. Do you want to continue?`);
    }

    if (isUnqueue) {
      return renderMessage('Removing file from Queue. Do you want to continue?');
    }

    if (isPriceOverriden) {
      return renderMessage('Amounts have changed, please recalculate to continue.');
    }
    if (drawerData.eventData?.isReopened) {
      return renderMessage(
        'Resubmitted files will not be eligible for payment until an invoice is generated, to ensure proper credits are applied.',
        'warning',
      );
    }
  };

  // Interface for stat code dropdown options
  interface StatCodeOption {
    name: string;
    value: string;
  }

  const filterStatCode = (statCode: PolicyField) => {
    const options = statCode?.options || [];
    const filteredOption: StatCodeOption[] = options.map((x: PolicyFieldOption) => {
      return { name: x.text, value: x.value };
    });

    // Sort numerically (matching Jackets implementation from ADO 1860333)
    // Fixes ANS-2408: TX Stat Code 1000 appearing at end instead of top
    // Updated to handle codes with numeric prefixes like "1300 TX"
    filteredOption.sort((a: StatCodeOption, b: StatCodeOption) => {
      // Extract numeric prefix from codes like "1300 TX" or "1000"
      const aMatch = String(a.value).match(/^(\d+)/);
      const bMatch = String(b.value).match(/^(\d+)/);

      const aNumeric = aMatch ? parseInt(aMatch[1], 10) : null;
      const bNumeric = bMatch ? parseInt(bMatch[1], 10) : null;

      // If both have numeric prefixes, compare them
      if (aNumeric !== null && bNumeric !== null) {
        if (aNumeric !== bNumeric) {
          return aNumeric - bNumeric;
        }
        // If numeric parts are equal (e.g., "1300" and "1300 TX"), sort by full value
        return String(a.value).localeCompare(String(b.value));
      }

      // If only one has numeric prefix, numeric comes first
      if (aNumeric !== null) return -1;
      if (bNumeric !== null) return 1;

      // If both are non-numeric, compare as strings (alphabetically)
      return String(a.value).localeCompare(String(b.value));
    });

    let newValue = '';
    if (filteredOption.length === 1) {
      newValue = filteredOption[0].value;
    } else {
      newValue = filteredOption.find((x: StatCodeOption) => x.value === statCode.value)?.value ?? '';
    }
    return { newValue, filteredOption };
  };

  const updateCalcCriteriaData = async (
    type: string,
    key: string,
    val: any,
    index?: any,
    subIndex?: any,
    isCheckBox = false,
  ) => {
    const obj = _.cloneDeep(calcCriteriaData);

    if (type === 'updateFileLevelSettings') {
      obj.fields = obj.fields.map((item: any) => {
        if (item.name === key) {
          return { ...item, value: val };
        }
        return item;
      });

      // logic for 'RateEffectiveDate' change
      if (key === 'RateEffectiveDate') {
        const payload: BulkRateTypePayload = {
          accountNumber: obj.accountNumber,
          state: obj.propertyState,
          county: obj.propertyCounty,
          effectiveDate: formatDate(val),
          jackets: obj.jacketRates.map((item: Policy) => {
            return {
              formNumber: item.formNumber,
              isExtendedCoverage:
                item.fields.find((x: PolicyField) => x.name === 'IsExtendedCoverage')?.isChecked ?? false,
            };
          }),
        };
        if (payload.effectiveDate) {
          await fetchBulkRateTypes(payload, obj.jacketRates);
        }
      }
    }

    if (type === 'updatePolicySettings' && obj?.jacketRates?.[index]?.fields) {
      obj.jacketRates[index].fields = obj.jacketRates[index].fields.map((item: any, fieldIndex: any) => {
        if (item.name === key && fieldIndex === subIndex) {
          return { ...item, value: val, ...(isCheckBox ? { isChecked: val === true, value: String(val) } : {}) };
        }
        return item;
      });

      if (key === 'IsExtendedCoverage') {
        obj.jacketRates[index].fields.forEach((item: any) => {
          if (item.name === 'RateType') {
            const isNewOptionExist =
              item.options.findIndex((rate: any) => rate.isExtendedCoverage === val && rate.value === item.value) !==
              -1;
            item.value = isNewOptionExist ? item.value : '';
          }
        });
      }
      if (key === 'RateType') {
        obj.jacketRates[index].fields.forEach((item: PolicyField) => {
          if (item.name === 'StatCode') {
            item.value = filterStatCode(item).newValue;
          }
        });
      }

      if (key === 'PolicyDate') {
        const ifRateEffectiveDateExists = obj.fields.filter((x: PolicyField) => x.name === 'RateEffectiveDate')[0]
          .value;
        if (!ifRateEffectiveDateExists) {
          const payload: SingleRateTypePayload = {
            formNumber: obj.jacketRates[index].formNumber,
            stateCode: obj.propertyState,
            policyEffectiveDate: formatDate(val),
            isExtendedCoverage:
              obj.jacketRates[index].fields.find((x: PolicyField) => x.name === 'IsExtendedCoverage')?.isChecked ??
              false,
            countyName: obj.propertyCounty,
            accountNumber: obj.accountNumber,
          };
          if (payload.policyEffectiveDate) {
            await fetchRateTypes(payload, obj.jacketRates[index].fields);
          }
        }
      }
    }

    if (type === 'updateCPLSettings' && obj?.cplRates?.[index]?.fields?.[0]) {
      obj.cplRates[index].fields[0].value = val;
    }

    if (type === 'updateQuestions' && obj?.[key]?.[index]?.questions?.[subIndex]) {
      obj[key][index].questions[subIndex].value = val;
      obj[key][index].questions[subIndex].isAnswered = !!val;
    }
    if (type === 'updateStandAloneQuestions' && obj?.[key]?.questions?.[index]) {
      obj[key].questions[index].value = val;
      obj[key].questions[index].isAnswered = !!val;
    }
    setCalcCriteriaData(obj);
  };

  const validateFileLevelSetting = async (field: any) => {
    const errs: FieldValidationError[] = (await doValidate(field, FileLevelSettings, jacketValidationContext)) || [];
    setFileLevelValidationError(errs);
  };

  useEffect(() => {
    if (calcCriteriaData?.jacketRates) {
      calcCriteriaData.jacketRates.forEach((policy: any, index: number) => {
        const updatedPolicy = { ...policy };
        const rateType = updatedPolicy.fields.filter((item: any) => item.name === 'RateType')[0];
        const currentOption = rateType.options.filter((item: any) => item.value === rateType.value)[0];
        const validateExtraFields = currentOption?.text?.toLowerCase()?.includes('reissue') ?? false;

        if (!validateExtraFields) {
          const fieldsToBeIgnore = ['PriorLiabilityAmount', 'PriorPolicyDate'];
          updatedPolicy.fields = updatedPolicy.fields.filter((item: any) => !fieldsToBeIgnore.includes(item.name));
        }
        validateErrors(updatedPolicy, index);
      });
    }
  }, [calcCriteriaData.jacketRates]);

  useEffect(() => {
    if (calcCriteriaData.fields) {
      const fields: any = {};
      calcCriteriaData.fields.forEach((field: any) => {
        fields[field.name] = {
          name: field.name,
          label: field.label,
          dataType: field.dataType,
          value: field.value,
          options: field.options ?? [],
          isRequired: field.isRequired ?? false,
        };

        if (field.name === 'RateEffectiveDate') {
          const policyDate: string[] = [];
          calcCriteriaData.jacketRates.forEach((jacket: any) => {
            const currentPolicyDate = jacket.fields.filter((field: any) => field.name === 'PolicyDate')[0];
            if (currentPolicyDate) {
              policyDate[policyDate.length] = currentPolicyDate.value;
            }
          });

          const ifAnyDateInvalid = policyDate.some(
            (currentDate: any) => !currentDate || !isValidDate(new Date(currentDate)),
          );

          if (policyDate.length > 0 && !ifAnyDateInvalid) {
            fields[field.name].isRequired = Array.from(new Set(policyDate)).length !== 1;
          }
        }
      });

      // Check if RateEffectiveDate is required and missing using the computed isRequired and actual field value
      const rateEffectiveDateField = fields['RateEffectiveDate'];
      if (rateEffectiveDateField) {
        const actualField = calcCriteriaData.fields.find((field: any) => field.name === 'RateEffectiveDate');
        setIsRateEffectiveDateMissing(rateEffectiveDateField.isRequired && !actualField?.value);
      }

      validateFileLevelSetting(fields);
    }
  }, [calcCriteriaData.jacketRates, calcCriteriaData.fields]);

  const renderCalculateCriteriaMessage = () => {
    if (hasCalcCriteriaTabChanges && isCalcCriteriaDiscard) {
      const action: React.ReactNode = (
        <IconButton
          aria-label="close"
          color="inherit"
          data-testid="RatesAndFeesDiscardChanges"
          size="small"
          onClick={handleValueReset}
        >
          <Typography className={classes.bold}>Discard Changes</Typography>
        </IconButton>
      );

      return renderMessage('You have unsaved changes on this page.', 'warning', action);
    }

    if (!calcCriteriaData?.propertyCounty) {
      return renderMessage(ratesFeesCountyRequired, 'error');
    }

    if (!isFormValid) {
      return renderMessage('Missing fields above are required to calculate and report.', 'error');
    }
  };

  const handleDrawerTabChange = (_e: any, v: any) => {
    if (drawerData.noRateAndFeeExists) {
      setTabValue && setTabValue(v);
      return;
    }
    if (v === 1) {
      if (tabValue === 0 && isPriceOverriden) {
        setDiscardError(true);
        setUnqueue(false);
        setReport({
          isReport: false,
          fromReport: false,
        });
      } else {
        setDiscardError(false);
        setTabValue && setTabValue(v);
      }
    } else {
      if (tabValue === 1 && hasCalcCriteriaTabChanges) {
        setCalcCriteriaDiscard(true);
      } else {
        setCalcCriteriaDiscard(false);
        setTabValue(v);
      }
    }
  };

  const primaryQaAttribute = React.useMemo(() => {
    if (tabValue === 1) {
      return 'CalcCriteriaCalculate';
    }
    if (isUnqueue && !showCalculateButton) {
      return 'RatesAndFeesYesRemove';
    }
    if (!showCalculateButton) {
      if (isUnqueue) return 'RatesAndFeesYesRemove';
      if (reportDetails.isReport) return 'RatesAndFeesYesReport';
      if (reportDetails.fromReport) return 'ReportsYesReport';
    }
    if (showCalculateButton || isDiscardError) {
      return 'RatesAndFeesCalculate';
    }
    return 'RatesAndFeesReport&Pay';
  }, [isUnqueue, isDiscardError, showCalculateButton, tabValue]);

  const calculateRateAndFees = () => {
    if (hasNetError(rateAndFeeData?.rateFeeLineItems ?? [])) {
      setShowValidation(true);
      setUnqueue(false);
      setReport({
        isReport: false,
        fromReport: false,
      });
      setDiscardError(false);
    } else {
      executeCalculate().then();
    }
  };

  const handlePrimaryAction = () => {
    if (tabValue === 1) {
      if (isFormValid) {
        if (drawerData.fileId) {
          const decodedFileId = HashUtils.decode(drawerData.fileId);

          // Save virtual jackets if any exist
          const saveVirtualJackets =
            virtualJackets.length > 0
              ? assignVirtualJacketsToFile(decodedFileId)
                  .then(() => {
                    setVirtualJackets([]);
                    addGlobalMsg({
                      message: `Successfully assigned ${virtualJackets.length} virtual jacket(s).`,
                      type: 'success',
                    });
                  })
                  .catch((error) => {
                    addGlobalMsg({
                      message: 'There was an error assigning virtual jackets.',
                      type: 'error',
                    });
                    console.error('Error assigning virtual jackets:', error);
                  })
              : Promise.resolve();

          // Save CPL fees if any exist
          const saveCplFees =
            cplFees.length > 0
              ? assignCplsToFileApi(decodedFileId).then(() => {
                  setCplFees([]);
                })
              : Promise.resolve();

          // Execute calc criteria after all saves complete
          Promise.all([saveVirtualJackets, saveCplFees]).then(() => {
            executeCalcCriteria();
          });
        }
      }
    } else if (tabValue === 2) {
      if (nropAmountsData.some((nrop) => nrop.isDirty)) {
        executeUpdateFileNrop().then(() => {
          nropAmountsData.forEach((nrop) => {
            nrop.isDirty = false;
          });
        });
      }
      if (nropAmountsData.some((nrop) => nrop.isDeleted)) {
        executeDeleteFileNrop().then(() => {
          const filteredNrops = nropAmountsData.filter((nrop) => !nrop.isDeleted);
          setNropAmountsData(filteredNrops);
        });
      }
      if (fileMisc) {
        executeUpdateFileMisc();
      }
    } else if (primaryActionLabel === 'Yes, Report') {
      executeReportData();
    } else if (primaryActionLabel === 'Calculate') {
      // Save fileMisc first if FSC user has made changes to shared services
      if (isFscUser && isFileMiscDirty && fileMisc) {
        executeUpdateFileMisc().then(() => {
          setIsFileMiscDirty(false);
          calculateRateAndFees();
        });
      } else {
        calculateRateAndFees();
      }
    } else if (isUnqueue) {
      executeUnqueue();
    } else if (primaryActionLabel === 'Report & Pay') {
      setReport({
        isReport: true,
        fromReport: false,
      });
    } else {
      closeDrawer();
    }
  };

  const goToStandAlone = () => {
    setTabValue(1);
    setDrawerData((prev: any) => ({
      ...prev,
      noRateAndFeeExists: false,
      calcCriteriaData: {
        ...prev.calcCriteriaData,
        standAloneEndorsementRates: {
          endorsements: [],
          fields: [],
          questions: [],
        },
      },
    }));
    setCalcCriteriaData((prev: any) => ({
      ...prev,
      standAloneEndorsementRates: {
        endorsements: [],
        fields: [],
        questions: [],
      },
    }));
  };

  const goToCpl = () => {
    closeDrawer();
    setFileTab?.(2);
    history.push(`/files/${drawerData.fileId}/cpl`);
  };

  const goToCalcCriteria = () => {
    handleDrawerTabChange(null, 1);
  };
  const showLoader =
    (tabValue === 1 && executeCalcCriteriaStatus === APIStatus.pending) ||
    (tabValue === 0 &&
      ((executeReportDataStatus === APIStatus.pending && primaryActionLabel === 'Yes, Report') ||
        (executeCalculateStatus === APIStatus.pending && primaryActionLabel === 'Calculate'))) ||
    (tabValue === 2 &&
      (getFileNropsByFileIdStatus === APIStatus.pending ||
        updateFileNropStatus === APIStatus.pending ||
        deleteFileNropStatus === APIStatus.pending ||
        updateFileMiscStatus === APIStatus.pending));

  const isSplitPercentageInvalid = useMemo(() => {
    if (!fileMisc?.ncsFile) return false;
    const total =
      (fileMisc.escrowSplit ?? 0) +
      (fileMisc.agentSplit ?? 0) +
      (fileMisc.stateAgencySplit ?? 0) +
      (fileMisc.nationalAgentSplit ?? 0);
    const isZero = Math.abs(total) < 0.0000005;
    const isHundred = Math.abs(total - 100) < 0.0000005;
    return !isZero && !isHundred;
  }, [fileMisc]);

  const hideRateAndFeeTable = !isPricingValid || isRateEffectiveDateMissing;

  const isPricingNotValid = tabValue === 0 && hideRateAndFeeTable;

  const handleToggle = () => {
    const newSwitchValue = !rateAndFeeData?.isAutoReportOnHold;
    setRateAndFeeData((prev: any) => ({ ...prev, isAutoReportOnHold: newSwitchValue }));
    executeAutoReportToggle().then();
  };

  useEffect(() => {
    const toggleButtonStatus =
      rateAndFeeData?.hasRemittedProduct &&
      rateAndFeeData?.remittedLineItems?.length > 0 &&
      fileData?.fileStatus !== orderStatuses['open'];
    setShowRemittedProduct(toggleButtonStatus);
  }, [rateAndFeeData?.hasRemittedProduct, tabValue, fileData?.fileStatus]);

  const showRemittedProductToggle = rateAndFeeData?.hasRemittedProduct && rateAndFeeData?.remittedLineItems?.length > 0;

  const renderReportedProductsSection = () => {
    if (!showRemittedProductToggle) return null;

    return (
      <>
        <div>
          <FormControlLabel
            control={
              <Switch
                checked={showRemittedProduct ?? false}
                onChange={() => setShowRemittedProduct((prev) => !prev)}
                name="Reported Products"
                size="small"
              />
            }
            label="Reported Products"
            labelPlacement="start"
            value={showRemittedProduct ?? false}
            className={classes.switch}
          />
          {showRemittedProduct && (
            <Typography className={classes.remittedProductMessage}>
              Refer to the table below for a complete list of all previously reported products in this file.
            </Typography>
          )}
        </div>
        {showRemittedProduct && (
          <ReportedFeesTable
            onChangeProduct={onChangeProduct}
            products={rateAndFeeData?.remittedLineItems ?? []}
            displayNet={rateAndFeeData?.displayNet ?? true}
            displayRemittanceBasis={rateAndFeeData?.isRemittanceBasisRate === true}
            showValidation={false}
            qaPrefix="RateAndFeesRemittedProduct"
            disableFields={true}
            hasRemittedProduct={true}
          />
        )}
      </>
    );
  };

  const fetchFileNropRecords = () => {
    if (drawerData.fileId) {
      const decodedFileId = HashUtils.decode(drawerData.fileId);
      executeGetFileNropsByFileId(decodedFileId);
    }
  };

  useEffect(() => {
    // Fetch fileMisc for FSC tab or for Rates & Fees tab when user is FSC role
    const shouldFetchFileMisc = tabValue === 2 || (tabValue === 0 && profile?.roleId === FSC_ROLE_ID);

    if (tabValue === 2) {
      if (getFileNropsByFileIdStatus !== APIStatus.pending) {
        fetchFileNropRecords();
      }
    }
    if (shouldFetchFileMisc && drawerData.fileId && getFileMiscStatus !== APIStatus.pending) {
      executeGetFileMisc(HashUtils.decode(drawerData.fileId));
    }
  }, [tabValue, profile?.roleId]);

  useEffect(() => {
    if (updateFileNropStatus === APIStatus.success) {
      fetchFileNropRecords();
    }
  }, [updateFileNropStatus]);

  useEffect(() => {
    if (getFileMiscStatus === APIStatus.success && getFileMiscValue) {
      setFileMisc(getFileMiscValue);
      setIsFileMiscDirty(false);
    }
  }, [getFileMiscStatus]);

  useEffect(() => {
    if (getFileNropsByFileIdStatus === APIStatus.success && getFileNropsByFileIdValue) {
      const nropDataFromApi: NropAmountsType[] = getFileNropsByFileIdValue.map((nrop) => ({
        id: makeRandomId(),
        fileId: nrop.fileId,
        fileNropId: nrop.fileNropId,
        grossPremium: nrop.grossPremium,
        grossPremiumInput: nrop.grossPremium?.toString() ?? '0',
        netPremium: nrop.netPremium,
        netPremiumInput: nrop.netPremium?.toString() ?? '0',
        liabilityAmount: nrop.liabilityAmount,
        liabilityAmountInput: nrop.liabilityAmount?.toString() ?? '0',
      }));
      setNropAmountsData(nropDataFromApi);
    }
  }, [getFileNropsByFileIdValue, getFileNropsByFileIdStatus]);

  // Shared Service splits calculation helpers
  const totalSplitPercentage = useMemo(() => {
    if (!fileMisc) return 0;
    return (
      (fileMisc.escrowSplit ?? 0) +
      (fileMisc.agentSplit ?? 0) +
      (fileMisc.stateAgencySplit ?? 0) +
      (fileMisc.nationalAgentSplit ?? 0)
    );
  }, [fileMisc]);

  const isSplitTotalValid = Math.abs(totalSplitPercentage - 100) < NEAR_ZERO_TOLERANCE;

  const handleSplitChange = (_field: SplitFieldKey, value: string) => {
    setSplitEditValue(value);
    setIsFileMiscDirty(true);
  };

  const handleSplitFocus = (field: SplitFieldKey) => {
    setSplitEditingField(field);
    const currentValue = fileMisc?.[field] ?? 0;
    setSplitEditValue(currentValue === 0 ? '' : String(currentValue));
  };

  const handleSplitBlur = (field: SplitFieldKey) => {
    const numValue = splitEditValue === '' ? 0 : parseFloat(splitEditValue);
    const finalValue = isNaN(numValue) ? 0 : Math.round(numValue * DECIMAL_PRECISION) / DECIMAL_PRECISION;

    setFileMisc((prev) => ({
      ...prev,
      fileId: prev?.fileId ?? (drawerData.fileId ? HashUtils.decode(drawerData.fileId) : 0),
      [field]: finalValue,
    }));
    setIsFileMiscDirty(true);

    setSplitEditingField(null);
    setSplitEditValue('');
  };

  const handleSharedServicesToggle = (checked: boolean) => {
    setFileMisc((prev) => ({
      ...prev,
      fileId: prev?.fileId ?? (drawerData.fileId ? HashUtils.decode(drawerData.fileId) : 0),
      ncsFile: checked,
      ...(checked
        ? {}
        : {
            escrowSplit: 0,
            agentSplit: 0,
            stateAgencySplit: 0,
            nationalAgentSplit: 0,
          }),
    }));
    setIsFileMiscDirty(true);
  };

  const formatSplitPercentage = (value: number | undefined): string => {
    if (value === undefined || value === null) return '0.000000';
    return value.toFixed(6);
  };

  const formatSplitCurrency = (value: number): string => {
    return value.toLocaleString('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const calculateSplitAmount = (percentage: number | undefined): number => {
    if (percentage === undefined || percentage === null) return 0;
    return (grossTotal.gross * percentage) / 100;
  };

  const totalSplitAmount = useMemo(() => {
    return (grossTotal.gross * totalSplitPercentage) / 100;
  }, [grossTotal.gross, totalSplitPercentage]);

  const rateAndFeesNode: ReactNode = (
    <>
      {(drawerData.rateAndFeeStatus === APIStatus.loading || drawerData.calcCriteriaStatus === APIStatus.loading) && (
        <div style={{ marginTop: 8 }}>
          <LoadingSpinner variant="circle" status={APIStatus.pending} />
        </div>
      )}
      {drawerData.rateAndFeeStatus === APIStatus.success && drawerData.calcCriteriaStatus === APIStatus.success && (
        <>
          {hideRateAndFeeTable ? (
            <>
              <div className={classes.noProducts}>
                <NoRatesIcon />
                <Grid>
                  <Typography className={classes.text}>
                    Rates cannot be calculated. Please add required information.
                  </Typography>
                </Grid>
                <Grid>
                  <AgentNetButton
                    data-testid="RatesAndFeesAddCalcCriteria"
                    onClick={goToCalcCriteria}
                    color="primary"
                    variant="contained"
                    size="small"
                  >
                    Add Calculation Criteria
                  </AgentNetButton>
                </Grid>
              </div>
              {renderReportedProductsSection()}
            </>
          ) : (
            <>
              <div className={classes.autoReport}>
                <Typography
                  className={classes.priceOverridenMsg}
                  style={{ visibility: isPriceOverriden ? 'visible' : 'hidden' }}
                  variant="body2"
                  color="textSecondary"
                >
                  User entered amounts are highlighted
                </Typography>

                {rateAndFeeData?.isAutoReportingEnabled ? (
                  <AgentNetCheckBox
                    label="Exclude from Auto-Reporting"
                    value={rateAndFeeData?.isAutoReportOnHold}
                    checkHandler={handleToggle}
                    disabled={
                      fileData?.fileStatus !== orderStatuses['open'] || executeAutoReportToggleStatus === 'pending'
                    }
                  />
                ) : null}
              </div>

              {/* Shared Services Section - above products, visible only to FSC users (role 14) */}
              {isFscUser && (
                <div className={classes.sharedServicesSection}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={fileMisc?.ncsFile ?? false}
                        onChange={(e) => handleSharedServicesToggle(e.target.checked)}
                        name="sharedServices"
                        size="small"
                        disabled={disableFields || getFileMiscStatus === APIStatus.pending}
                      />
                    }
                    label="Shared Service Transaction Splits"
                    labelPlacement="start"
                    className={classes.switch}
                    data-testid="rates-and-fees-shared-services-toggle"
                  />
                  {fileMisc?.ncsFile && (
                    <Table className={classes.splitsTable} size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell className={classes.splitNameCell}>Name</TableCell>
                          <TableCell className={classes.splitPercentageCell}>Percentage</TableCell>
                          <TableCell className={classes.splitAmountCell}>Amount</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {SPLIT_FIELDS.map(({ key, label }) => (
                          <TableRow key={key}>
                            <TableCell className={classes.splitNameCell}>{label}</TableCell>
                            <TableCell className={classes.splitPercentageCell}>
                              <div className={classes.splitPercentageContent}>
                                <input
                                  className={classes.splitInput}
                                  type="number"
                                  step="any"
                                  min="0"
                                  max="100"
                                  value={
                                    splitEditingField === key ? splitEditValue : formatSplitPercentage(fileMisc[key])
                                  }
                                  onChange={(e) => handleSplitChange(key, e.target.value)}
                                  onFocus={() => handleSplitFocus(key)}
                                  onBlur={() => handleSplitBlur(key)}
                                  disabled={disableFields || getFileMiscStatus === APIStatus.pending}
                                  data-testid={`rates-and-fees-${key}-input`}
                                />
                                <span>%</span>
                              </div>
                            </TableCell>
                            <TableCell className={classes.splitAmountCell}>
                              {formatSplitCurrency(calculateSplitAmount(fileMisc[key]))}
                            </TableCell>
                          </TableRow>
                        ))}
                        <TableRow className={classes.totalRow}>
                          <TableCell className={classes.splitNameCell}>Total</TableCell>
                          <TableCell
                            className={`${classes.splitPercentageCell} ${
                              !isSplitTotalValid ? classes.totalInvalid : ''
                            }`}
                          >
                            {formatSplitPercentage(totalSplitPercentage)}%
                          </TableCell>
                          <TableCell className={classes.splitAmountCell}>
                            {formatSplitCurrency(totalSplitAmount)}
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  )}
                </div>
              )}

              <ReportedFeesTable
                onChangeProduct={onChangeProduct}
                onClear={onClear}
                products={rateAndFeeData?.rateFeeLineItems ?? []}
                displayNet={rateAndFeeData?.displayNet ?? true}
                displayRemittanceBasis={rateAndFeeData?.isRemittanceBasisRate === true}
                showValidation={showValidation}
                qaPrefix="RateAndFees"
                disableFields={disableFields}
              />
              {rateAndFeeData?.notes && (
                <div {...(showRemittedProductToggle && { className: classes.notesContainer })}>
                  <Typography className={classes.notesHeading}>Notes</Typography>
                  {rateAndFeeData?.notes.map((notes: string, index: any) => (
                    <Typography key={index} className={classes.notesMsg}>
                      {notes}
                    </Typography>
                  ))}
                  <Typography className={classes.policyNetPremiumNotes}>
                    {isTotalPolicyNetPremiumZero &&
                      'Please override the $0 Gross Reported amount for each policy then calculate to enable reporting of this file.'}
                  </Typography>
                </div>
              )}

              {/* Reported Products Section */}
              {showRemittedProductToggle && (
                <div>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={showRemittedProduct ?? false}
                        onChange={() => setShowRemittedProduct((prev) => !prev)}
                        name="Reported Products"
                        size="small"
                      />
                    }
                    label="Reported Products"
                    labelPlacement="start"
                    className={classes.switch}
                  />
                  {showRemittedProduct && (
                    <>
                      <Typography className={classes.remittedProductMessage}>
                        Refer to the table below for a complete list of all previously reported products in this file.
                      </Typography>
                      <ReportedFeesTable
                        onChangeProduct={onChangeProduct}
                        products={rateAndFeeData?.remittedLineItems ?? []}
                        displayNet={rateAndFeeData?.displayNet ?? true}
                        displayRemittanceBasis={rateAndFeeData?.isRemittanceBasisRate === true}
                        showValidation={false}
                        qaPrefix="RateAndFeesRemittedProduct"
                        disableFields={true}
                        hasRemittedProduct={true}
                      />
                    </>
                  )}
                </div>
              )}
              {!disableFields && warningMessages()}
            </>
          )}
        </>
      )}
    </>
  );
  const calculatedNode: ReactNode = (
    <>
      {drawerData.calcCriteriaStatus === APIStatus.loading && (
        <div style={{ marginTop: 8 }}>
          <LoadingSpinner variant="circle" status={APIStatus.pending} />
        </div>
      )}
      {drawerData.calcCriteriaStatus === APIStatus.success && (
        <>
          <CalcCriteriaTab
            updateJacketEndorsement={updateJacketEndorsement}
            calcCriteriaData={calcCriteriaData}
            updateCalcCriteriaData={updateCalcCriteriaData}
            updateSaEndorsement={updateSaEndorsement}
            filterStatCode={filterStatCode}
            additionalQuestionRef={additionalQuestionRef}
            shouldShowValidation
            validationErrors={validationError}
            fileLevelValidationError={fileLevelValidationError}
            disableFields={disableFields || executeCalcCriteriaStatus === APIStatus.pending}
            savedJacketData={drawerData?.calcCriteriaData?.jacketRates ?? []}
            cplFees={cplFees}
            setCplFees={setCplFees}
            onValidationChange={handleCalcCriteriaValidationChange}
            virtualJackets={virtualJackets}
            setVirtualJackets={setVirtualJackets}
          />
          {!disableFields && renderCalculateCriteriaMessage()}
        </>
      )}
    </>
  );

  const { hasPermission } = useNavPermissions();

  return (
    <FormDrawerComponent
      title={`File No: ${fileName}`}
      open={openDrawer}
      width={960}
      disableEnforceFocus
      primaryActionProps={{
        disabled:
          ((drawerData.eventData?.isReopened || grossTotal.net === 0) && primaryActionLabel === 'Report & Pay') ||
          drawerData.noRateAndFeeExists ||
          showLoader ||
          (isTotalPolicyNetPremiumZero && primaryActionLabel === 'Report & Pay') ||
          (tabValue === 2 && isSplitPercentageInvalid) ||
          (tabValue === 0 && isFscUser && isSplitPercentageInvalid),
        loading: showLoader,
        'data-testid': primaryQaAttribute,
      }}
      dismissActionProps={{
        'data-testid': 'RatesAndFeesCancel',
      }}
      crossQAPrefix="RatesAndFees"
      primaryActionLabel={primaryActionLabel}
      primaryActionTootip={
        primaryActionLabel === 'Report & Pay' && grossTotal.net === 0 && !isTotalPolicyNetPremiumZero
          ? 'You can Report $0.00 but you cannot pay $0.00.'
          : ''
      }
      isPaddingRightZero
      hidePrimaryAction={
        (tabValue === 1 && drawerData.eventData?.isReopenedByProduct && !hasCalcCriteriaTabChanges) ||
        drawerData.calcCriteriaStatus === APIStatus.loading ||
        (tabValue === 0 && isMoreInfoRequired) ||
        disableFields ||
        isPricingNotValid ||
        drawerData.noRateAndFeeExists ||
        (hideReportsAndPay && primaryActionLabel === 'Report & Pay') ||
        (drawerData.resource === 'order-management' && primaryActionLabel === 'Report & Pay')
      }
      onPrimaryAction={handlePrimaryAction}
      onDismissAction={handleCancel}
      actionItems={actionItems}
      showBackDropOnDrawer={drawerData.showBackDropOnDrawer ?? false}
      dismissActionLabel="Done"
    >
      {
        <>
          <Paper square elevation={0} className={classes.tabWrap}>
            <Tabs
              value={parseInt((tabValue || 0) as unknown as string)}
              indicatorColor="primary"
              textColor="primary"
              onChange={handleDrawerTabChange}
              className={classes.tabsRoot}
            >
              <Tab
                label={
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <Typography variant="body1" className={classes.tabText}>
                      Rates & Fees
                    </Typography>
                  </div>
                }
                data-testid="RatesAndFeesTab"
              />
              <Tab
                data-testid="CalcCriteriaTab"
                label={
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <Typography variant="body1" className={classes.tabText}>
                      Calc. Criteria
                    </Typography>
                    {showProblemIcon && <ReportProblemOutlined style={{ marginLeft: 5 }} />}
                  </div>
                }
              />
              {isFscTabEnabled?.value && hasPermission.orderManagementDashboard && drawerData.fileId && (
                <Tab
                  data-testid="reports-and-pay-drawer-tabs-fsc"
                  label={
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      <Typography variant="body1" className={classes.tabText}>
                        FSC
                      </Typography>
                    </div>
                  }
                />
              )}
            </Tabs>
          </Paper>

          {drawerData.noRateAndFeeExists ? (
            drawerData.calcCriteriaStatus === APIStatus.loading || drawerData.rateAndFeeStatus === APIStatus.loading ? (
              <div style={{ marginTop: 8 }}>
                <LoadingSpinner variant="circle" status={'pending'} />
              </div>
            ) : (
              <div className={classes.noProducts}>
                <NoProducts />
                {disableFields ? (
                  <Grid>
                    <Typography className={classes.text}>No Products Found</Typography>
                  </Grid>
                ) : (
                  <>
                    <Grid>
                      <Typography className={classes.text}>No Products Found</Typography>
                      <Typography className={classes.subtext}>
                        At least one remittable product is required to price a file.
                      </Typography>
                    </Grid>
                    <Grid>
                      <Grid className={classes.actions}>
                        <AgentNetButton
                          onClick={goToCpl}
                          data-testid="RatesAndFeesAddNewCPL"
                          color="primary"
                          variant="contained"
                          size="small"
                        >
                          Add New CPL
                        </AgentNetButton>
                        {hasPermission.jackets && (
                          <AgentNetButton
                            onClick={goToJacket}
                            data-testid="RatesAndFeesAddNewJacket"
                            color="primary"
                            variant="contained"
                            size="small"
                          >
                            Add New Jacket
                          </AgentNetButton>
                        )}
                      </Grid>
                      <AgentNetButton
                        onClick={goToStandAlone}
                        className={classes.standAlone}
                        data-testid="RatesAndFeesAddSAEndo"
                        variant="text"
                        size="small"
                      >
                        Add Standalone Endorsement
                      </AgentNetButton>
                    </Grid>
                  </>
                )}
              </div>
            )
          ) : tabValue === 0 ? (
            rateAndFeesNode
          ) : tabValue === 1 ? (
            calculatedNode
          ) : tabValue === 2 && drawerData.fileId ? (
            <FSCTab
              fileId={HashUtils.decode(drawerData.fileId)}
              stateCode={drawerData.rateAndFeeData?.propertyState}
              nropAmountsData={nropAmountsData}
              setNropAmountsData={setNropAmountsData}
              fileMiscData={fileMisc}
              setFileMiscData={setFileMisc}
            />
          ) : null}
        </>
      }
    </FormDrawerComponent>
  );
};

export default ReportsAndPayDrawer;
